// components/EmptyBatchState.tsx
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, Calendar, BookOpen, ListChecks, UserCheck } from "lucide-react";
import { Card } from "@/components/ui/card";

interface EmptyBatchStateProps {
    batchName: string;
    topic?: string;
}

export default function EmptyBatchState({ batchName, topic }: EmptyBatchStateProps) {
    return (
        <div className="space-y-4">
            {/* Header - Exact same as real leaderboard */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col items-center justify-center gap-3 mb-6"
            >
                <h2 className="text-2xl text-center font-extrabold">{batchName}</h2>
                <p className="text-md  text-slate-400 text-center">
                    {topic ? `Currently Learning : ${topic}` : "Batch Ready"}
                </p>
            </motion.div>

            <div className="grid gap-4">
                <AnimatePresence mode="popLayout">
                    <motion.div
                        layout
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: -100 }}
                        transition={{
                            layout: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.3 },
                            scale: { duration: 0.3 },
                        }}
                    >
                        <Card
                            className="p-3 bg-white/10
             shadow-md transition-all duration-300 group rounded-2xl 
             dark:bg-white/20">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    {/* Gold Trophy Badge */}
                                    <motion.div
                                        whileHover={{ scale: 1.1, rotate: [0, -8, 8, 0] }}
                                        whileTap={{ scale: 0.95 }}
                                        className="relative flex items-center justify-center w-12 h-12 rounded-full 
                               bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 
                               shadow-xl"
                                    >
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                                            className="absolute inset-0 rounded-full opacity-70"
                                            style={{
                                                background: "conic-gradient(from 0deg, transparent, rgba(255,215,0,0.7), transparent)",
                                            }}
                                        />
                                        <Trophy className="w-8 h-8 text-white relative z-10" />
                                    </motion.div>

                                    {/* Message */}
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">No Students Yet</h3>
                                        <p className="text-md text-gray-700 mt-1 flex items-center gap-2">
                                            Waiting for the first champion
                                        </p>
                                    </div>
                                </div>

                                {/* Score */}
                                <div className="text-right">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                                        className="text-5xl font-black bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent"
                                    >
                                        —
                                    </motion.div>
                                    <p className="text-sm text-gray-400 font-medium">Total Score</p>
                                </div>
                            </div>

                            {/* Real Icons + Placeholder Stats Grid */}
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
                                }}
                                className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]"
                            >
                                {/* Attendance */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar size={18} className="text-blue-600" />
                                        <span className="text-xs font-semibold text-gray-900">Attendance</span>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-700">—</div>
                                    <div className="text-xs text-gray-600 mt-1">Waiting for data</div>
                                </motion.div>

                                {/* Assignments */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <BookOpen size={18} className="text-green-600" />
                                        <span className="text-xs font-semibold text-gray-900">Assignments</span>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-700">—</div>
                                    <div className="text-xs text-gray-600 mt-1">Waiting for data</div>
                                </motion.div>

                                {/* Tasks */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <ListChecks size={18} className="text-purple-600" />
                                        <span className="text-xs font-semibold text-gray-900">Tasks</span>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-700">—</div>
                                    <div className="text-xs text-gray-600 mt-1">Waiting for data</div>
                                </motion.div>

                                {/* Interviews */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <UserCheck size={18} className="text-orange-600" />
                                        <span className="text-xs font-semibold text-gray-900">Interviews</span>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-700">—</div>
                                    <div className="text-xs text-gray-600 mt-1">Waiting for data</div>
                                </motion.div>
                            </motion.div>
                        </Card>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}